import { MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { CoreBox, CoreFormGroup, CoreFormControlLabel, CoreSwitch, CoreTypography } from '@youscience/core';
import { Picture, classifications, TopTenMajor, SchoolWithScholarships } from '@common-types';
import FavoriteOutlinedIcon from '@mui/icons-material/FavoriteOutlined';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import SpeakerNotesOutlinedIcon from '@mui/icons-material/SpeakerNotesOutlined';

import { CollegeLocation } from '@interfaces/location';

import { SchoolSurveyModalProps, withSchoolSurveyModal } from '@HOCs/withSchoolSurveyModal';

import { getImageSrc } from '@utils/schoolPhoto';
import { getDefaultSchoolDescription } from '@utils/description';
import { getBreadcrumbs } from '@utils/breadcrumbs';

import useSchoolFavoritesStore from '@store/SchoolFavoritesStore';
import useHomePageStore from '@store/HomePageStore';
import useOptInStore from '@store/OptInStore';
import useUserPreferencesStore from '@store/UserPreferencesStore';

import { type PageSectionId, PageSectionIds, SIDEBAR_DATA } from '@constants/schoolDetailsPage';
import { BREADCRUMBS_PAGE_TEXT } from '@constants/breadcrumbs';
import { STATE_APPLY_LINKS, STATE_SPOTLIGHT_BREADCRUMB_ROOT } from '@constants/stateSpotlight';
import { ROUTES } from '@constants/ROUTES';

import { Breadcrumbs } from '@components/Breadcrumbs';
import { IPEDS } from '@components/IPEDS';
import { AreaProgramsAccordion } from '@components/AreaProgramsAccordion';
import ActionButton from '@components/ActionButton';
import { UtahBadge } from '@components/UtahBadge';

import defaultSchoolDetailsPhoto from '@images/default-school-details.png';
import CommonAppIcon from '@images/common-app.svg?react';

import { GeneralInformation } from './components/GeneralInformation';
import { DegreesAwarded } from './components/DegreesAwarded';
import { TuitionCost } from './components/TuitionCost';
import { Admissions } from './components/Admissions';
import { StudentOutcomes } from './components/StudentOutcomes';
import { SchoolOffers } from './components/SchoolOffers';
import { OptInConnectionConfirmation } from './components/OptInConnectionConfirmation';
import RelatedScholarships from './components/RelatedScholarships';
import { InterestMatchedPrograms } from './components/InterestMatchedPrograms';

import {
  sxStyles,
  StyledHeadSectionActions,
  StyledWebsiteButton,
  StyledHeadSectionInner,
  StyledHeadSectionInfo,
  StyledHeadSectionTitle,
  StyledSchoolPhotoWrap,
  StyledMainSectionInner,
  StyledSidebarWrap,
  StyledSidebarInner,
  StyledSidebarItemInner,
  StyledSidebarItemTitle,
  SectionLinkOffset,
} from './SchoolDetails.styles';
import { useScrollSpy } from '@hooks/useScrollSpy';
import { useOnScrollEnd } from '@hooks/useOnScrollEnd';

import { UserRoles } from '@interfaces/user';
import { useUserProfile } from '@hooks/useUserProfile';
import { useStateSpotlight } from '@hooks/useStateSpotlight';
import { useInstitutionScholarships } from '@hooks/useInstitutionScholarships';
import { useProgramFits } from '@hooks/useProgramFits';

type SchoolDetailsProps = SchoolSurveyModalProps;
type SectionRef = MutableRefObject<HTMLAnchorElement | null>;

const SchoolDetails = (props: SchoolDetailsProps) => {
  const { onOpenSurvey } = props;
  const location: CollegeLocation = useLocation();
  const params = useParams();

  const { schoolFavorites, userPreferences } = useUserPreferencesStore((state) => state);
  const { toggleSchoolFavorite, isLoading } = useSchoolFavoritesStore((state) => state);
  const selectedSchoolName = useHomePageStore((state) => state.landingPageSchoolData?.cardInfo?.name);
  const { adminHomePageData, getAdminHomePageData } = useHomePageStore((state) => state);

  const saveUserConnection = useOptInStore((state) => state.saveUserConnection);
  const schoolId = parseInt(params.id as string, 10);

  const { spotlight } = useStateSpotlight();
  const isStatePublicCollege = !!spotlight?.publicCollegeIds.includes(schoolId);
  const stateApplyLink = spotlight && isStatePublicCollege ? STATE_APPLY_LINKS[spotlight.stateCode] : undefined;

  const [isShowLessDescription, setIsShowLessDescription] = useState(false);
  const [isSchoolConnected, setIsSchoolConnected] = useState(false);
  const [isShowConnectionConfirmation, setIsShowConnectionConfirmation] = useState(false);

  const school = adminHomePageData?.school ?? ({} as SchoolWithScholarships);

  const [selectedSection, setSelectedSection] = useState<PageSectionId | null>(null);
  const [currentScrolledSection, setCurrentScrolledSection] = useState<PageSectionId | null>(null);

  const sectionRefs: Record<PageSectionId, SectionRef> = {
    [PageSectionIds['about']]: useRef(null),
    [PageSectionIds['general-information']]: useRef(null),
    [PageSectionIds['areas-of-study']]: useRef(null),
    [PageSectionIds['degrees-awarded']]: useRef(null),
    [PageSectionIds['admissions']]: useRef(null),
    [PageSectionIds['tuition-cost-aid']]: useRef(null),
    [PageSectionIds['scholarship']]: useRef(null),
    [PageSectionIds['students-outcomes']]: useRef(null),
    [PageSectionIds['extracurricular-activities']]: useRef(null),
  };

  const onSectionIntersect = (entry: IntersectionObserverEntry) => {
    setSelectedSection(null);
    setCurrentScrolledSection(entry.target.getAttribute('id') as PageSectionId);
  };

  useScrollSpy(Object.values(sectionRefs), onSectionIntersect);

  const handleSectionLink = (sectionId: PageSectionId) => {
    const sectionRef = sectionRefs[sectionId];
    if (!sectionRef.current) {
      return;
    }

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useOnScrollEnd(() => {
      setCurrentScrolledSection(null);
      setSelectedSection(sectionId);
    });

    sectionRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const {
    id,
    memberId,
    picture,
    directory,
    description = '',
    completions,
    characteristics,
    collegeScorecard,
    enrollment,
    scholarships = [],
  } = school;

  const schoolUrl = directory?.links.url;
  const schoolPhotoUrl = getImageSrc(id, defaultSchoolDetailsPhoto, picture as Picture);
  const schoolWebsiteLink = schoolUrl?.match(/^[a-zA-Z]+:\/\//) ? schoolUrl : `https://${schoolUrl}`;
  const shortSchoolDescription = description.slice(0, 400);
  const topTenMajors = collegeScorecard?.topTenMajors as TopTenMajor[];
  const programCipCodes = useMemo(
    () => [...new Set(completions?.map((completion) => completion.cipCode) ?? [])],
    [completions],
  );

  const { data: institutionScholarships } = useInstitutionScholarships(schoolId, isStatePublicCollege);
  const { data: interestMatchedPrograms = [] } = useProgramFits(programCipCodes, isStatePublicCollege);
  const shownScholarships = isStatePublicCollege
    ? (institutionScholarships?.scholarships ?? scholarships)
    : scholarships;

  const isStudentOutcomesData =
    school?.collegeScorecard?.latest?.student?.retention_rate?.four_year?.full_time ||
    school?.collegeScorecard?.latest?.completion?.completion_rate_4yr_150nt_pooled ||
    school?.collegeScorecard?.latest?.earnings?.['6_yrs_after_entry']?.median;

  const isSchoolOffersData =
    characteristics?.hasStudyAbroad ||
    characteristics?.acceptsAPCredit ||
    characteristics?.acceptsCreditForLifeExperience ||
    characteristics?.acceptsDualEnrollment ||
    characteristics?.acceptsNoCredit ||
    characteristics?.hasAirForceROTC ||
    characteristics?.hasArmyROTC ||
    characteristics?.hasROTC ||
    characteristics?.hasNavyROTC ||
    characteristics?.hasRemedialService ||
    characteristics?.hasCounselingService ||
    characteristics?.hasEmploymentService ||
    characteristics?.hasPlacementService ||
    characteristics?.hasDayCareService;

  const { role } = useUserProfile();

  const currentUserIsAdmin = role === UserRoles.Admin;

  const homePageName =
    adminHomePageData?.ipedsId && currentUserIsAdmin ? BREADCRUMBS_PAGE_TEXT.ADMIN_HOME_PAGE : selectedSchoolName;

  const pageBreadcrumbs = spotlight
    ? {
        links: [
          { to: ROUTES.SCHOOLS, text: STATE_SPOTLIGHT_BREADCRUMB_ROOT },
          { to: ROUTES.SCHOOLS, text: BREADCRUMBS_PAGE_TEXT.SCHOOLS },
        ],
        currentPage: BREADCRUMBS_PAGE_TEXT.SCHOOL_DETAILS,
      }
    : getBreadcrumbs(location.state?.from, BREADCRUMBS_PAGE_TEXT.SCHOOL_DETAILS, homePageName);

  useEffect(() => {
    const currentId = parseInt(params.id as string, 10);

    if (currentId !== adminHomePageData?.ipedsId) {
      void getAdminHomePageData(params.id as string);
    }
  }, [params.id, adminHomePageData?.ipedsId, getAdminHomePageData]);

  useEffect(() => {
    const initialSchoolConnectionStatus = !!userPreferences.tenantSharing?.find(
      (school) => school.collegeId === schoolId && school.currentlySharing,
    );

    setIsSchoolConnected(initialSchoolConnectionStatus);
  }, [userPreferences.tenantSharing, schoolId]);

  const handleSetSchoolData = (isOpenFromNotes: boolean) => {
    onOpenSurvey?.({ id, name: directory?.name, calculateLink: directory?.links.priceCalculatorUrl }, isOpenFromNotes);
  };

  const handleOpenSurveyNotes = () => {
    handleSetSchoolData(true);
  };

  const isSchoolFavorite = useMemo(() => {
    return !!schoolFavorites.find((schoolFavorites) => schoolFavorites.school.id === schoolId);
  }, [schoolFavorites, params.id]);

  const isShowConnectionToggle = useMemo(() => {
    return !!schoolFavorites.find((school) => school.id === schoolId);
  }, [schoolFavorites, params.id]);

  const handleAddToFavorites = async () => {
    if (isLoading) return;

    await toggleSchoolFavorite(school);

    if (!isSchoolFavorite) {
      handleSetSchoolData(false);
    } else {
      saveUserConnection(id, false);
    }
  };

  const handleShowMoreDescription = () => {
    setIsShowLessDescription((prevState) => !prevState);
  };

  const getSchoolDescription = () => {
    if (!description) {
      return getDefaultSchoolDescription(directory, enrollment?.totalStudentsEnrolled || 0, collegeScorecard);
    }

    if (description?.length <= 400) return description;

    return (
      <>
        {isShowLessDescription ? description : `${shortSchoolDescription}...`} &nbsp;
        <CoreTypography sx={sxStyles.showMoreButton} onClick={handleShowMoreDescription} component="span">
          {isShowLessDescription ? 'show less' : 'show more'}
        </CoreTypography>
      </>
    );
  };

  const getAreasOfStudy = () => {
    const areasCipCodes = completions?.map((area) => Math.floor(+area.cipCode));

    return areasCipCodes?.filter((el, index) => areasCipCodes.indexOf(el) === index) || [];
  };

  const handleChangeSchoolConnection = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setIsSchoolConnected(checked);

    if (checked) {
      setIsShowConnectionConfirmation(true);
    } else {
      saveUserConnection(schoolId, false);
    }
  };

  return (
    <>
      <CoreBox sx={sxStyles.headSectionBg}>
        <StyledHeadSectionInner>
          <StyledHeadSectionInfo>
            <Breadcrumbs links={pageBreadcrumbs.links} currentPage={pageBreadcrumbs.currentPage} />

            <StyledHeadSectionTitle variant="h2" component="h1">
              {directory?.name}
            </StyledHeadSectionTitle>

            {isStatePublicCollege && (
              <CoreBox data-testid="schoolHeaderBadge" sx={sxStyles.utahBadgeWrap}>
                <UtahBadge variant="ushe" />
              </CoreBox>
            )}

            <StyledHeadSectionActions>
              <ActionButton
                data-testid={`${isSchoolFavorite ? 'removeFrom' : 'addTo'}FavoritesButton`}
                startIcon={isSchoolFavorite ? <FavoriteOutlinedIcon /> : <FavoriteBorderOutlinedIcon />}
                onClick={handleAddToFavorites}
              >
                {isSchoolFavorite ? 'Remove from favorites' : 'Add to favorites'}
              </ActionButton>

              {isSchoolFavorite && (
                <ActionButton
                  startIcon={<SpeakerNotesOutlinedIcon />}
                  variant="outlined"
                  onClick={handleOpenSurveyNotes}
                >
                  Review your notes
                </ActionButton>
              )}

              <a target="_blank" href={schoolWebsiteLink}>
                <StyledWebsiteButton startIcon={<DashboardCustomizeOutlinedIcon />} variant="text">
                  Website
                </StyledWebsiteButton>
              </a>

              {stateApplyLink && (
                <a data-testid="stateApplyLink" target="_blank" rel="noopener noreferrer" href={stateApplyLink.url}>
                  <ActionButton variant="outlined" startIcon={<OpenInNewOutlinedIcon />}>
                    {stateApplyLink.label}
                  </ActionButton>
                </a>
              )}

              {memberId ? <CommonAppIcon /> : null}
            </StyledHeadSectionActions>

            {isShowConnectionToggle && userPreferences.tenantSharing && (
              <CoreFormGroup>
                <CoreFormControlLabel
                  control={<CoreSwitch checked={isSchoolConnected} onChange={handleChangeSchoolConnection} />}
                  label="Manage my connection with this school"
                />
              </CoreFormGroup>
            )}
          </StyledHeadSectionInfo>

          <StyledSchoolPhotoWrap>
            <img src={schoolPhotoUrl} alt="schools nearby view" />
          </StyledSchoolPhotoWrap>
        </StyledHeadSectionInner>
      </CoreBox>

      <CoreBox sx={sxStyles.mainSectionBg}>
        <StyledMainSectionInner>
          <StyledSidebarWrap>
            <StyledSidebarInner>
              {SIDEBAR_DATA.map((item) => {
                const isSelected = selectedSection === item.value;
                const sectionLink = `#${PageSectionIds[item.value]}` as const;

                if (item.value === PageSectionIds['areas-of-study'] && !getAreasOfStudy().length) return null;

                if (item.value === PageSectionIds['degrees-awarded'] && !topTenMajors) return null;

                if (item.value === PageSectionIds.scholarship && !shownScholarships?.length) return null;

                if (item.value === PageSectionIds['students-outcomes'] && !isStudentOutcomesData) return null;

                if (item.value === PageSectionIds['extracurricular-activities'] && !isSchoolOffersData) return null;

                return (
                  <div
                    key={item.id}
                    onClick={(e) => {
                      e.preventDefault();
                      handleSectionLink(item.value);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {' '}
                    <StyledSidebarItemInner
                      selected={isSelected || currentScrolledSection === item.value}
                      value={item.value}
                    >
                      <CoreBox>{item.icon}</CoreBox>

                      <StyledSidebarItemTitle>{item.title}</StyledSidebarItemTitle>
                    </StyledSidebarItemInner>
                  </div>
                );
              })}
            </StyledSidebarInner>
          </StyledSidebarWrap>

          <CoreBox sx={sxStyles.mainSectionContentWrap}>
            <CoreBox sx={sxStyles.aboutSectionWrap}>
              <SectionLinkOffset id={PageSectionIds.about} ref={sectionRefs.about} sx={sxStyles.linkOffset(350)} />
              <CoreTypography sx={sxStyles.aboutSectionTitle} variant="h3" component="h2">
                About
              </CoreTypography>

              <CoreTypography>{getSchoolDescription()}</CoreTypography>
            </CoreBox>

            <GeneralInformation scrollToRef={sectionRefs['general-information']} school={school} />

            {Boolean(getAreasOfStudy().length) && (
              <CoreBox>
                <SectionLinkOffset id={PageSectionIds['areas-of-study']} ref={sectionRefs['areas-of-study']} />
                <CoreTypography sx={sxStyles.areasOfStudySectionTitle} variant="h3" component="h2">
                  Areas of study and programs offered
                </CoreTypography>

                <InterestMatchedPrograms programs={isStatePublicCollege ? interestMatchedPrograms : []} />

                {getAreasOfStudy().map((area) => {
                  const currentArea = classifications.find((classification) => {
                    if (classification.cipCode === +area) {
                      return classification.cipTitle;
                    }
                    return '';
                  });

                  const areaPrograms = completions.reduce((accumulator, currentValue) => {
                    if (area === Math.floor(+currentValue.cipCode)) {
                      accumulator.push(+currentValue.cipCode);
                    }

                    return accumulator;
                  }, [] as number[]);

                  return (
                    <AreaProgramsAccordion
                      key={area}
                      areaTitle={currentArea?.cipTitle}
                      areaCipCode={area}
                      areaPrograms={areaPrograms}
                    />
                  );
                })}
              </CoreBox>
            )}

            <DegreesAwarded scrollToRef={sectionRefs['degrees-awarded']} topTenMajors={topTenMajors} />

            <Admissions scrollToRef={sectionRefs.admissions} school={school} />

            <TuitionCost scrollToRef={sectionRefs['tuition-cost-aid']} school={school} />

            <RelatedScholarships
              scholarships={shownScholarships}
              scrollToRef={sectionRefs.scholarship}
              schoolName={directory?.name}
            />

            <StudentOutcomes
              scrollToRef={sectionRefs['students-outcomes']}
              school={school}
              hasData={Boolean(isStudentOutcomesData)}
            />

            <SchoolOffers
              scrollToRef={sectionRefs['extracurricular-activities']}
              school={school}
              hasData={Boolean(isSchoolOffersData)}
            />
          </CoreBox>
        </StyledMainSectionInner>
      </CoreBox>

      <IPEDS />

      <OptInConnectionConfirmation
        schoolInfo={{ id: schoolId, name: directory?.name }}
        isShowConnectionConfirmation={isShowConnectionConfirmation}
        initialShareStatus={isSchoolConnected}
        setIsShowConnectionConfirmation={setIsShowConnectionConfirmation}
        setIsSchoolConnected={setIsSchoolConnected}
      />
    </>
  );
};

export const SchoolDetailsWithModal = withSchoolSurveyModal(SchoolDetails);
